import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FaTrophy, FaRedo, FaSignOutAlt, FaBolt, FaChevronDown, FaChevronUp } from 'react-icons/fa';
import { useSyncDuelSocket } from '../../contexts/SyncDuelSocketContext.tsx';
import PlayerName from '../Common/PlayerName.tsx';
import sfx from '../../lib/goofyAudio.ts';
import './SyncDuelGameOver.css';

export default function SyncDuelGameOver() {
  const { code } = useParams();
  const navigate = useNavigate();
  const { state, leave } = useSyncDuelSocket();
  const [expandedPlayers, setExpandedPlayers] = useState<Set<string>>(new Set());

  const players = state?.players || [];
  const winnerId = state?.end?.winner;
  const sorted = useMemo(() => (
    [...players].sort((a, b) => (b.score || 0) - (a.score || 0))
  ), [players]);
  const maxScore = sorted.length ? Math.max(...sorted.map(p => p.score || 0)) : 1;
  // Le vainqueur est soit celui défini par le serveur, soit le premier du classement
  const winner = sorted.find(p => p.id === winnerId) || sorted[0];
  // Le vrai winnerId est soit celui du serveur, soit l'id du premier du classement
  const actualWinnerId = winnerId || winner?.id;
  const categoryKey = state?.end?.categoryKey || state?.categoryKey;
  const categoryName = state?.end?.categoryName || state?.categoryName;
  const categoryEmoji = state?.categoryEmoji;
  const totals = state?.end?.totals || {};
  const fastest = state?.end?.fastest;
  const maxQuestions = state?.end?.maxQuestions || state?.total;

  // Récupérer toutes les catégories uniques jouées depuis perPlayer.byCategory
  const playedCategories = useMemo(() => {
    const categoriesMap = new Map<string, { name: string; emoji: string }>();
    const perPlayer = state?.end?.perPlayer || {};
    
    Object.values(perPlayer).forEach((playerData: any) => {
      if (playerData?.byCategory) {
        Object.entries(playerData.byCategory).forEach(([catKey, catData]: [string, any]) => {
          if (!categoriesMap.has(catKey)) {
            categoriesMap.set(catKey, {
              name: catData.categoryName || catKey,
              emoji: catData.categoryEmoji || ''
            });
          }
        });
      }
    });
    
    return Array.from(categoriesMap.entries()).map(([key, data]) => ({
      key,
      name: data.name,
      emoji: data.emoji
    }));
  }, [state?.end?.perPlayer]);

  const handleBack = () => {
    if (code) leave(code);
    try { sfx.leave(); } catch {}
    navigate('/', { replace: true });
  };

  const togglePlayerDetails = (playerId: string) => {
    setExpandedPlayers(prev => {
      const next = new Set(prev);
      if (next.has(playerId)) {
        next.delete(playerId);
      } else {
        next.add(playerId);
      }
      return next;
    });
  };

  // Play a win/lose jingle based on whether current user is the winner
  useEffect(() => {
    const myName = (localStorage.getItem('syncduel:name') || '').toString();
    const me = players.find(p => p.name === myName) || players[0];
    if (!me) return;
    if (winner && me.id === winner.id) {
      try { sfx.win(); } catch {}
    } else {
      try { sfx.lose(); } catch {}
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Save recent SyncDuel match summary locally for stats view
  useEffect(() => {
    try {
      const recKey = 'syncduel:recent';
      const prev = JSON.parse(localStorage.getItem(recKey) || '[]');
      const entry = {
        at: new Date().toISOString(),
        code: code || '',
        categoryKey,
        categoryName,
        emoji: categoryEmoji,
        winnerId: winnerId || (winner?.id),
        maxQuestions: maxQuestions || 10,
        players: (players || []).map(p => ({
          id: p.id,
          name: p.name,
          score: p.score || 0,
          correct: (totals && totals[p.id]?.correct) || 0,
          answered: (totals && totals[p.id]?.answered) || 0,
        })),
      };
      const next = [entry, ...prev].slice(0, 10);
      localStorage.setItem(recKey, JSON.stringify(next));
    } catch {}
    // run once on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="syncduel-gameover-container">
      <div className="syncduel-gameover-box">
        {/* Header */}
        <div className="syncduel-gameover-header">
          <h1 className="syncduel-gameover-title">Fin de partie</h1>
          <div className="syncduel-gameover-meta">
            <span>Salon: {code}</span>
            {playedCategories.length > 0 && (
              <>
                <span className="syncduel-gameover-meta-divider">•</span>
                <div className="syncduel-gameover-categories">
                  {playedCategories.map((cat) => (
                    <span key={cat.key} className="syncduel-badge">
                      {(cat.emoji || '') + ' '}{cat.name}
                    </span>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Winner highlight */}
        {winner && (
          <div className="syncduel-winner-card">
            <div className="syncduel-winner-content">
              <div className="syncduel-winner-label">
                <FaTrophy className="syncduel-winner-icon" />
                <span className="syncduel-winner-label-text">Vainqueur</span>
              </div>
              <div className="syncduel-winner-player">
                <div className="syncduel-winner-avatar">{winner.name.charAt(0).toUpperCase()}</div>
                <span className="syncduel-winner-name"><PlayerName userId={winner.uid || winner.id} userName={winner.name} checkEffect={!!winner.uid} /></span>
              </div>
              <span className="syncduel-winner-score">{winner.score ?? 0} pts</span>
            </div>
          </div>
        )}

        {/* Scoreboard */}
        <div className="syncduel-scoreboard">
          {sorted.map((p, i) => {
            const ratio = Math.max(0, Math.min(100, Math.round(100 * (p.score || 0) / Math.max(1, maxScore))));
            const isWinner = p.id === actualWinnerId;
            const t = totals[p.id] || { correct: 0, answered: 0 };
            const totalQ = Math.max(1, Number(maxQuestions) || 1);
            const isExpanded = expandedPlayers.has(p.id);
            const playerStats = (state?.end?.perPlayer?.[p.id] || {}) as { 
              correct?: number; 
              answered?: number; 
              byCategory?: Record<string, { correct: number; answered: number; categoryName?: string; categoryEmoji?: string }> 
            };
            
            return (
              <div key={p.id} className={`syncduel-score-row ${isWinner ? 'winner' : ''}`}>
                <div className="syncduel-score-row-header">
                  <div className="syncduel-score-row-player">
                    <span className={`syncduel-score-rank ${i === 0 ? 'first' : 'other'}`}>{i + 1}</span>
                    <div className="syncduel-score-avatar">{p.name.charAt(0).toUpperCase()}</div>
                    <span className="syncduel-score-name"><PlayerName userId={p.uid || p.id} userName={p.name} checkEffect={!!p.uid} /></span>
                  </div>
                  <div className="syncduel-score-badges">
                    <span className="syncduel-score-badge points">{p.score ?? 0} pts</span>
                    <span className="syncduel-score-badge correct">{t.correct || 0}/{totalQ} justes</span>
                    <button 
                      className="syncduel-expand-button"
                      onClick={() => togglePlayerDetails(p.id)}
                      title="Voir les détails"
                    >
                      {isExpanded ? <FaChevronUp /> : <FaChevronDown />}
                    </button>
                  </div>
                </div>
                <div className="syncduel-score-progress">
                  <div 
                    className={`syncduel-score-progress-bar ${isWinner ? 'winner' : 'other'}`}
                    style={{ width: `${ratio}%` }}
                  />
                </div>
                
                {/* Détails expandables */}
                {isExpanded && (
                  <div className="syncduel-player-details">
                    <div className="syncduel-details-title">📊 Détails par catégorie</div>
                    <div className="syncduel-details-grid">
                      {playerStats.byCategory && Object.entries(playerStats.byCategory).map(([catKey, stats]: [string, any]) => {
                        const catCorrect = stats.correct || 0;
                        const catTotal = stats.answered || 0;
                        const catPercent = catTotal > 0 ? Math.round((catCorrect / catTotal) * 100) : 0;
                        const catName = stats.categoryName || catKey;
                        const catEmoji = stats.categoryEmoji || '📁';
                        
                        return (
                          <div key={catKey} className="syncduel-detail-item">
                            <div className="syncduel-detail-category">
                              <span className="syncduel-detail-emoji">{catEmoji}</span>
                              <span className="syncduel-detail-name">{catName}</span>
                            </div>
                            <div className="syncduel-detail-stats">
                              <span className="syncduel-detail-score">{catCorrect}/{catTotal}</span>
                              <div className="syncduel-detail-bar">
                                <div 
                                  className="syncduel-detail-bar-fill"
                                  style={{ width: `${catPercent}%` }}
                                />
                              </div>
                              <span className="syncduel-detail-percent">{catPercent}%</span>
                            </div>
                          </div>
                        );
                      })}
                      {(!playerStats.byCategory || Object.keys(playerStats.byCategory).length === 0) && (
                        <div className="syncduel-no-details">Aucune donnée disponible</div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Fastest answer */}
        {fastest && (
          <div className="syncduel-fastest-card">
            <div className="syncduel-fastest-content">
              <FaBolt className="syncduel-fastest-icon" />
              <span>
                Plus rapide à répondre : <b><PlayerName userId={players.find(p => p.id === fastest.playerId)?.uid || fastest.playerId} userName={players.find(p => p.id === fastest.playerId)?.name || '—'} checkEffect={!!players.find(p => p.id === fastest.playerId)?.uid} /></b>
                {" "}en <b>{new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format((fastest.ms || 0) / 1000)} s</b>
              </span>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="syncduel-actions">
          <button className="syncduel-action-button primary" onClick={() => navigate(`/syncduel/${code}`)}>
            <FaRedo />
            <span>Rejouer</span>
          </button>
          <button className="syncduel-action-button secondary" onClick={handleBack}>
            <FaSignOutAlt />
            <span>Quitter</span>
          </button>
        </div>
      </div>
    </div>
  );
}
